import React, { useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/marketing/BrandLogo";
import { LayoutGrid, Bot, MessagesSquare, Mic, Phone, PhoneCall, Users, CalendarDays, CreditCard, BarChart3, Settings as SettingsIcon, LogOut, Shield, Plus, MessageSquare } from "lucide-react";

const NAV = [
  { to: "", end: true, label: "Overview", Icon: LayoutGrid, testid: "nav-overview" },
  { to: "agents", label: "Agents", Icon: Bot, testid: "nav-agents" },
  { to: "conversations", label: "Conversations", Icon: MessagesSquare, testid: "nav-conversations" },
  { to: "voice", label: "Web Voice", Icon: Mic, testid: "nav-voice" },
  {
    to: "calls", label: "Calls & Campaigns", Icon: Phone, testid: "nav-calls",
    children: [
      { to: "calls/assistants",     label: "1 · Assistants",         testid: "nav-calls-assistants" },
      { to: "phone-numbers",        label: "2 · Twilio Numbers",     testid: "nav-phone-numbers-sub" },
      { to: "calls/outbound",       label: "3 · Outbound Campaigns", testid: "nav-calls-outbound" },
    ],
  },
  { to: "crm", label: "CRM", Icon: Users, testid: "nav-crm" },
  { to: "appointments", label: "Appointments", Icon: CalendarDays, testid: "nav-appointments" },
  { to: "analytics", label: "Analytics", Icon: BarChart3, testid: "nav-analytics" },
  { to: "billing", label: "Billing", Icon: CreditCard, testid: "nav-billing" },
  { to: "settings", label: "Settings", Icon: SettingsIcon, testid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, tenant, logout, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  // Keep credits live: refresh on every route change + every 25s + on focus.
  useEffect(() => { refresh?.(); }, [location.pathname, refresh]);
  useEffect(() => {
    const tick = () => refresh?.();
    const interval = setInterval(tick, 25_000);
    const onFocus = () => refresh?.();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(interval); window.removeEventListener("focus", onFocus); };
  }, [refresh]);

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      <aside className="w-64 bg-[#0F1115] text-white p-4 flex flex-col" data-testid="sidebar">
        <Link to="/dashboard" className="flex items-start gap-2 px-2 py-3 mb-4 text-white">
          <BrandLogo size="md" />
        </Link>
        <div className="px-2 -mt-3 mb-4 font-mono text-[10px] text-white/40 leading-tight">{tenant?.name || "Workspace"}</div>
        <nav className="flex-1 space-y-1 overflow-auto">
          {NAV.map(({ to, end, label, Icon, testid, children }) => (
            <React.Fragment key={label}>
              {(() => {
                // A parent nav is "active" when the current path matches
                // itself OR any of its declared children — so Phone Numbers
                // (a child of Calls & Campaigns) also highlights the parent
                // and keeps the submenu open.
                const childMatch = children && children.some((c) => location.pathname.startsWith(`/dashboard/${c.to}`));
                const showSub = childMatch || location.pathname.startsWith(`/dashboard/${to}`);
                return (
                  <>
                    <NavLink
                      to={to}
                      end={end}
                      data-testid={testid}
                      className={({ isActive }) => `sidebar-link ${isActive || showSub ? "active" : ""}`}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </NavLink>
                    {children && showSub && (
                      <div className="ml-6 border-l border-white/10 pl-2 py-1 space-y-0.5" data-testid={`${testid}-submenu`}>
                        {children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            data-testid={c.testid}
                            className={({ isActive }) => `sidebar-link text-[13px] py-1.5 ${isActive ? "active" : ""}`}
                          >
                            <span className="w-1 h-1 rounded-full bg-white/30" /> {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </React.Fragment>
          ))}
          {user?.role === "super_admin" && (
            <NavLink to="/admin" data-testid="nav-admin-link" className="sidebar-link mt-4 border-t border-white/10 pt-4">
              <Shield className="w-4 h-4" /> Super Admin
            </NavLink>
          )}
        </nav>
        <button
          data-testid="logout-button"
          onClick={() => { logout(); nav("/login"); }}
          className="sidebar-link text-red-300 hover:!bg-red-500/10"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-[#E2E8F0] bg-white px-8 py-4 flex items-center justify-between" data-testid="topbar">
          <div>
            <div className="font-mono text-[11px] text-[#4B5563] uppercase tracking-wide">{(tenant?.package_id || "free_forever").replace(/_/g, " ")} · pay-as-you-go</div>
            <div className="font-display font-bold text-lg">{user?.name}'s workspace</div>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono" data-testid="header-credits">
            <CreditPill label="Web Chat"    value={tenant?.ai_credits ?? 0}    unit="msgs"  color="#0044FF" />
            <CreditPill label="Web Voice"   value={tenant?.voice_credits ?? 0} unit="min"   color="#10B981" />
            <CreditPill label="AI Calling"  value={tenant?.voice_credits ?? 0} unit="min"   color="#7C3AED" />
            <div className="w-8 h-8 bg-[#0044FF] text-white flex items-center justify-center font-bold rounded-sm" data-testid="user-avatar">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function CreditPill({ label, value, unit, color }) {
  const low = (value || 0) < 50;
  return (
    <Link
      to="/dashboard/billing"
      className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-[6px] border border-[#E2E8F0] hover:border-[#0F1115] bg-white transition-colors"
      title={`${label} credits — click to top up`}
      data-testid={`credit-pill-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span className="text-[#4B5563] uppercase tracking-widest text-[10px]">{label}</span>
      <span className={`font-semibold ${low ? "text-[#EF4444]" : "text-[#0F1115]"}`}>{value}</span>
      <span className="text-[10px] text-[#94A3B8]">{unit}</span>
      <Plus className="w-3.5 h-3.5 text-[#94A3B8] group-hover:text-[#0044FF] transition-colors" />
    </Link>
  );
}
